import { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { device } from '../../utils/styled-component-mixins';
import useIsMounted from '../custom-hooks/useIsMounted';

const FeaturedCardLinkStyled = styled.a`
  --imageGradient: linear-gradient(
    180.14deg,
    rgba(0, 0, 0, 0) 21.26%,
    rgba(0, 0, 0, 0.56) 47.64%,
    rgba(0, 0, 0, 0.7) 99.82%
  );
  ${(props) =>
    props.backgroundImage &&
    `background-image: var(--imageGradient), url("${props.backgroundImage}");`}
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
  color: var(--white);
  display: flex;
  height: 35rem;
  margin-bottom: 2.5rem;
  padding: 3.2rem 2.4rem;
  position: relative;
  text-decoration: none;

  &:last-child {
    margin-bottom: 0;
  }

  @media ${device.desktop} {
    box-shadow: 0px 16px 80px rgba(91, 110, 127, 0.25);
    height: 43rem;
    margin-bottom: 0;

    &:last-child {
      margin-right: 0;
    }
  }

  @media ${device.desktopLg} {
    padding: 2.5rem 4rem;
  }
`;

const FeaturedCardArticle = styled.article`
  align-items: flex-end;
  display: flex;
  width: 100%;

  @media ${device.desktop} {
    display: block;
  }

  &.preload * {
    animation: none !important;
    transition: none !important;
  }

  .mounted.animation-wrapper {
    @media ${device.desktop} {
      height: 100%;
      padding-top: 0;

      &:after {
        background-color: var(--bdo-red);
        content: '';
        height: 100%;
        position: absolute;
        right: 300%;
        top: 0;
        transform: skewX(50deg);
        transition: all 0.3s ease-in;
        width: 500%;
        z-index: 1;
      }

      &.hover-in.mounted {
        animation: translate-right 0.3s forwards;

        &::after {
          animation: translate-right 0.3s forwards;
          background-color: var(--slate);
        }

        .cta {
          transform: translateX(0);
        }

        .featured-card-wrapper {
          .content-wrapper {
            bottom: 3.2rem;

            .featured-card-description {
              opacity: 1;
            }
          }
        }
      }

      &.hover-out.mounted {
        animation: translate-left 0.3s forwards;

        &::after {
          animation: translate-left 0.3s forwards;
        }

        .cta {
          --cta-translate-value: -88%;
          transform: translateX(var(--cta-translate-value));
        }

        .featured-card-wrapper {
          .content-wrapper {
            bottom: var(--bottom-position);

            .featured-card-description {
              transition-delay: 0;
              transition: all 0.2s ease-out;
            }
          }
        }
      }
    }
  }

  .cta {
    align-items: center;
    color: inherit;
    display: flex;
    font-size: 1.4rem;
    font-weight: bold;
    line-height: 1.8rem;
    position: relative;
    text-decoration: none;

    span:last-child {
      font-size: 3.2rem;
    }

    @media ${device.tablet} {
      transition: all 0.3s ease-in-out;

      span:first-child {
        overflow: hidden;
        transition: all 0.3s ease-in-out;
      }
    }

    @media ${device.desktop} {
      position: absolute;
    }
  }

  h3 {
    font-size: 1.6rem;
    font-weight: 700;
    line-height: 2.4rem;
    margin-bottom: 1.6rem;

    @media ${device.desktop} {
      font-size: 2.2rem;
      font-weight: 800;
      line-height: 3.2rem;
    }
  }

  .featured-card-wrapper {
    display: flex;
    flex-direction: column;
    height: 100%;
    justify-content: space-between;
    overflow: hidden;
    position: sticky;
    z-index: 100;

    @media ${device.desktop} {
      justify-content: flex-end;
    }
  }

  .content-wrapper {
    align-items: flex-start;
    display: flex;
    flex-direction: column;
    position: relative;
    transition: all 0.3s linear;
    z-index: 100;

    @media ${device.desktop} {
      --bottom-position: -15%;
      bottom: var(--bottom-position);
      margin-top: 0;
    }

    .featured-card-description {
      font-size: 1.4rem;
      margin-bottom: 3.3rem;
      margin-top: 0;
      opacity: 0;
      transition: all 0.2s linear;
      transition-delay: 0.2s;
      display: none;

      @media ${device.desktop} {
        display: block;
      }
    }
  }

  @keyframes translate-right {
    from {
      right: 300%;
    }

    to {
      right: -150%;
    }
  }

  @keyframes translate-left {
    from {
      right: -150%;
    }

    to {
      right: 300%;
    }
  }
`;

const LocationCard = (props) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isFirstRender, setIsFirstRender] = useState(true);
  const featuredCardRef = useRef(null);
  const ctaRef = useRef(null);
  const isMounted = useIsMounted();

  const getOwnTitleHeight = () => {
    const title = featuredCardRef.current.querySelector('h3');
    const titleStyles = getComputedStyle(title);
    const titleHeight =
      title?.getBoundingClientRect().height + parseInt(titleStyles.marginBottom, 10);
    props.getTitleHeightCallBack(titleHeight);
  };

  const setCtaTranslateValue = () => {
    const ctaText = ctaRef?.current?.querySelectorAll('span')?.[0];
    const ctaTextWidth = ctaText?.getBoundingClientRect().width;
    const ctaTextTranslateValue = ctaTextWidth + 10;
    ctaRef?.current?.style.setProperty('--cta-translate-value', `-${ctaTextTranslateValue}px`);
  };

  useEffect(() => {
    if (isFirstRender) {
      getOwnTitleHeight();
      setIsFirstRender(false);
    }

    if (document.readyState === 'complete') {
      setCtaTranslateValue();
    } else {
      window.addEventListener('load', setCtaTranslateValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FeaturedCardLinkStyled
      backgroundImage={props.cardBackgroundImage}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      href={props.url}
      aria-label={`${props.linkText}`}
      className="non-default-link"
    >
      <FeaturedCardArticle ref={featuredCardRef}>
        <div
          className={`animation-wrapper ${isFirstRender ? 'preload' : ''} ${
            isHovered ? 'hover-in' : 'hover-out'
          } ${isMounted && 'mounted'}`}
        >
          <div className={`featured-card-wrapper`}>
            <div className="content-wrapper">
              <h3>{props.cardHeading}</h3>
              <div className="featured-card-description">
                <div dangerouslySetInnerHTML={{ __html: props.cardDescription }} />
              </div>
            </div>
            <div
              ref={ctaRef}
              className="cta"
              href={props.url}
              aria-label={`Read more about ${props.pageTitle}`}
            >
              <span>{props.linkText}</span>
              <span className="material-icons-round" aria-hidden="true">
                chevron_right
              </span>
            </div>
          </div>
        </div>
      </FeaturedCardArticle>
    </FeaturedCardLinkStyled>
  );
};

export default LocationCard;
